import React from 'react'
import { ConsignmentBadge } from '../shared/ConsignmentBadge.jsx'

function rowThumb(images) {
  if (!images || images.length === 0) return null
  const src = images[0]
  if (src.includes('cdn.shopify.com')) {
    const sep = src.includes('?') ? '&' : '?'
    return `${src}${sep}width=200`
  }
  return src
}

function StatusBadge({ shopifyStatus, quantity }) {
  // Effective row state combines Shopify lifecycle + stock count.
  // Backend filters by this 4-state via ?status=, but the badge has to be
  // derived because the response still returns the raw shopify_status.
  if (shopifyStatus === 'draft') {
    return <span className="badge badge-info">Draft</span>
  }
  if (shopifyStatus === 'active' && (quantity ?? 0) > 0) {
    return <span className="badge badge-success">In stock</span>
  }
  if (shopifyStatus === 'active') {
    return <span className="badge badge-warning">Out of stock</span>
  }
  if (shopifyStatus === 'archived') {
    return <span className="badge badge-neutral">Archived</span>
  }
  if (!shopifyStatus) return null
  return <span className="badge badge-neutral">{shopifyStatus}</span>
}

function PutawayResults({ items, hasSearched, loading, error, onRowClick }) {
  if (loading) {
    return (
      <div className="card" style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <div className="loading-spinner" />
      </div>
    )
  }
  if (error) {
    return (
      <div className="card" style={{
        background: 'var(--color-error-light, #fee2e2)', padding: 16, marginBottom: 16
      }}>
        <strong>Error:</strong> {error}
      </div>
    )
  }
  if (!hasSearched) {
    return (
      <div className="card">
        <div className="empty-state">
          <div className="empty-state-title">Search inventory</div>
          <div className="empty-state-description">
            Enter a title, Shopify product id, or item id (AT-XXXXXXXX) above and tap Search.
          </div>
        </div>
      </div>
    )
  }
  if (items.length === 0) {
    return (
      <div className="card">
        <div className="empty-state">
          <div className="empty-state-title">No items found</div>
          <div className="empty-state-description">Try a different search term or status filter.</div>
        </div>
      </div>
    )
  }

  return (
    <div className="card">
      {/* On mobile we abandon table semantics entirely and render each row
           as a flex card. This sidesteps the global .table { display: block;
           overflow-x: auto } mobile rule and lets long titles wrap properly:
           min-width: 0 on the flex title cell is the key — without it,
           flex children default to min-width: auto and refuse to shrink
           below their content's intrinsic size. */}
      <style>{`
        .putaway-mobile-shelf { display: none; }
        @media (max-width: 768px) {
          .putaway-results-table,
          .putaway-results-table > tbody {
            display: block !important;
            width: 100%;
            overflow-x: hidden !important;
          }
          .putaway-results-table > thead {
            display: none;
          }
          .putaway-results-table > tbody > tr {
            display: flex !important;
            align-items: flex-start;
            gap: 12px;
            padding: 12px 8px;
            border-bottom: 1px solid var(--color-border);
            cursor: pointer;
          }
          .putaway-results-table > tbody > tr > td {
            display: block !important;
            padding: 0;
            border: none;
          }
          /* Image cell — fixed thumbnail size */
          .putaway-results-table > tbody > tr > td:first-child {
            flex: 0 0 auto;
            width: 48px;
          }
          /* Title cell — flexible, must shrink. min-width: 0 lets the flex
             child wrap long words instead of forcing parent width. */
          .putaway-results-table .col-mobile-primary {
            flex: 1 1 0;
            min-width: 0;
            word-break: break-word;
            overflow-wrap: anywhere;
          }
          .putaway-results-table .col-mobile-hide {
            display: none !important;
          }
          .putaway-mobile-shelf {
            display: block;
            margin-top: 4px;
            font-size: 14px;
            font-weight: 400;
            color: var(--color-text-secondary);
          }
        }
      `}</style>
      <table className="table putaway-results-table">
        <thead>
          <tr>
            <th style={{ width: 60 }}>Image</th>
            <th>Title</th>
            <th className="col-mobile-hide" style={{ textAlign: 'center', width: 56 }}>Qty</th>
            <th className="col-mobile-hide">Status</th>
            <th className="col-mobile-hide">Item</th>
            <th className="col-mobile-hide">Shopify</th>
            <th className="col-mobile-hide">Shelf</th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => {
            // Bay only — warehouse team asked to hide the L/R/slot
            // position from the row display.
            const loc = item.current_shelf_id || '—'
            const src = rowThumb(item.images)
            return (
              <tr
                key={item.item_id}
                onClick={() => onRowClick(item)}
                style={{ cursor: 'pointer' }}
              >
                <td>
                  {src ? (
                    <img
                      src={src}
                      alt=""
                      style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
                    />
                  ) : (
                    <div style={{
                      width: 48, height: 48, background: '#f3f4f6', borderRadius: 4,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, color: '#9ca3af'
                    }}>
                      No img
                    </div>
                  )}
                </td>
                <td className="col-mobile-primary" style={{ fontWeight: 500, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                  {item.title}
                  {item.ownership === 'consignment' && (
                    <span style={{ marginLeft: 8 }}><ConsignmentBadge splitPct={item.split_pct} /></span>
                  )}
                  {/* Mobile-only meta block: visible <=768px via global .mobile-row-meta
                       + local .putaway-mobile-shelf media query (below). */}
                  <div className="mobile-row-meta" style={{ display: 'none' }}>
                    <span><StatusBadge shopifyStatus={item.shopify_status} quantity={item.quantity} /></span>
                    <span>Qty: {item.quantity ?? '—'}</span>
                  </div>
                  <div className="putaway-mobile-shelf">
                    Shelf: <strong style={{ fontFamily: 'monospace' }}>{loc}</strong>
                  </div>
                </td>
                <td className="col-mobile-hide" style={{ textAlign: 'center' }}>{item.quantity ?? '—'}</td>
                <td className="col-mobile-hide"><StatusBadge shopifyStatus={item.shopify_status} quantity={item.quantity} /></td>
                <td className="text-sm text-muted col-mobile-hide" style={{ fontFamily: 'monospace' }}>
                  {item.item_id || '—'}
                </td>
                <td className="text-sm text-muted col-mobile-hide" style={{ fontFamily: 'monospace' }}>
                  {item.shopify_product_id}
                </td>
                <td className="text-sm col-mobile-hide" style={{ fontFamily: 'monospace' }}>
                  {loc}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default PutawayResults
